import { useCallback, useContext } from 'react'
import log from 'loglevel'
import Raw from '../services/Raw'
import Auth from '../context/Auth'
import LocaleContext from '../context/Locale'

// returns GET, POST, PUT, DELETE operations for a given API resource
export default function useRawPromise() {
    const { jwt, refreshJwt } = useContext(Auth)
    const { locale } = useContext(LocaleContext)

    // on a live 401, try one shared refresh and retry the call with the new jwt before
    // giving up - covers cases where the proactive refresh hasn't caught an expired jwt yet
    const callWithRetry = useCallback(async (method, resource, opts) => {

        const result = await doCall(
            method,
            resource,
            { ...opts, jwt: jwt.current, locale }
        )


        if (result.error?.status !== 401) return result

        try {
            const newJwt = await refreshJwt()
            return doCall(method, resource, { ...opts, jwt: newJwt, locale })
        } catch {
            return result
        }
    }, [jwt, locale, refreshJwt])

    /**
     * @param {string} resource
     * @param {{ query?: any, detailedError?: boolean, unpackHydra?: boolean }} [options]
     */
    const get = useCallback((resource, { query, detailedError, unpackHydra } = {}) => {
        return callWithRetry('GET', resource, { query, detailedError, unpackHydra })
    }, [callWithRetry])

    /**
     * @param {string} resource
     * @param {{ query?: any, detailedError?: boolean, body: any }} options
     */
    const post = useCallback((resource, { query, body, detailedError }) => {
        return callWithRetry('POST', resource, { query, body, detailedError })
    }, [callWithRetry])

    /**
     * @param {string} resource
     * @param {{ query?: any, detailedError?: boolean, body: any }} options
     */
    const put = useCallback((resource, { query, body, detailedError }) => {
        return callWithRetry('PUT', resource, { query, body, detailedError })
    }, [callWithRetry])

    /**
     * @param {string} resource
     * @param {{ query?: any, detailedError?: boolean }} [options]
     */
    const del = useCallback((resource, { query, detailedError } = {}) => {
        return callWithRetry('DELETE', resource, { query, detailedError })
    }, [callWithRetry])

    return {
        get,
        post,
        put,
        del,
    }
}

async function doCall(method, resource, { query, body, jwt, detailedError, locale, unpackHydra = true }) {

    try {
        const urlSearchParams = new URLSearchParams(query)
        const queryString = urlSearchParams.toString()
        const d = await Raw.fetchJson(method, `${resource}${query ? '?' : ''}${queryString}`, { jwt, body, returnOnError: detailedError, acceptLanguage: locale })
        // unpack 'hydra:member", if it exists
        if (!d) {
            return {}
        }
        const data = (Array.isArray(d['hydra:member']) && unpackHydra) ? d['hydra:member'] : d
        return { data }
    } catch (error) {
        log.error(error)
        return { error }
    }
}
